import { mezclarColores } from './colores.util';

/**
 * Paleta fija de los overlays del mapa de vehículos.
 *
 * Los colores deben ser distinguibles entre sí de un vistazo: la traza del
 * viaje se dibuja junto a los markers de paradas, exceso de velocidad y
 * eventos, así que NO puede depender del color del tema del cliente (antes
 * usaba el accent y quedaba igual que alguno de los markers).
 *
 * Los mismos valores se replican como clases `.marker-ref-*` en el panel de
 * búsqueda de viajes, para mostrar dentro de los inputs cómo se ve cada marker.
 */
export const COLOR_VIAJE = '#382AF8';
export const COLOR_PARADA = '#29B6F6';
export const COLOR_EXCESO = '#ef5350';
export const COLOR_EVENTO = '#9c29df';

/**
 * Puntos de reporte sobre la traza: rojo, para que se lean como algo distinto
 * de la línea del viaje (azul) en vez de confundirse con ella.
 */
export const COLOR_REPORTE = '#FD2626';

/**
 * Punto que se mueve al darle play al recorrido: se distingue tanto de la traza
 * como de los reportes por los que va pasando.
 */
export const COLOR_PUNTO_ANIMADO = '#2962FF';

/**
 * Color con el que arranca el degradado de la traza. Es un valor elegido, no el
 * complementario calculado: el complementario de un azul da un naranja que
 * grita de más.
 */
export const COLOR_DEGRADADO_INICIO = '#00D5FF';

/** Color del movimiento que corre sobre la traza. */
export const COLOR_MOVIMIENTO = '#8097F5';

/**
 * Paradas de color del modo "por velocidad" de la traza, de lento a rápido, en
 * km/h. Entre parada y parada el color se interpola, así la traza pasa de un
 * color al otro en degradado y no a saltos; por encima de la última queda su
 * color. El verde de lo lento está oscurecido para que no se pierda sobre el
 * mapa claro.
 */
export const ESCALA_VELOCIDAD: { velocidad: number; color: string }[] = [
  { velocidad: 0, color: '#2E7D32' },
  { velocidad: 20, color: '#7CB342' },
  { velocidad: 40, color: '#F9A825' },
  { velocidad: 60, color: '#FB8C00' },
  { velocidad: 80, color: '#E53935' },
  { velocidad: 100, color: '#8E0038' },
];

/** Velocidad mínima y máxima de un viaje, en km/h. */
export interface RangoVelocidad {
  min: number;
  max: number;
}

/** Rango de las velocidades dadas; `undefined` si no hay ninguna válida. */
export function rangoVelocidad(
  velocidades: number[],
): RangoVelocidad | undefined {
  // Con un loop y no `Math.min(...)`: un viaje largo tiene decenas de miles de
  // reportes y el spread de argumentos puede reventar el stack.
  let rango: RangoVelocidad | undefined;
  for (const v of velocidades) {
    if (!Number.isFinite(v)) continue;
    if (!rango) rango = { min: v, max: v };
    else {
      if (v < rango.min) rango.min = v;
      if (v > rango.max) rango.max = v;
    }
  }
  return rango;
}

/**
 * Color de la escala para una velocidad en km/h.
 *
 * Con `rango`, la escala es relativa al viaje: la mínima del viaje toma el
 * color de la primera parada y la máxima el de la última, y el resto se
 * reparte en proporción. Sirve para ver dónde anduvo más rápido o más lento
 * un viaje que entero cae en una sola zona de la escala fija (todo urbano, o
 * todo ruta).
 */
export function colorDeVelocidad(
  velocidad: number,
  rango?: RangoVelocidad,
): string {
  const primera = ESCALA_VELOCIDAD[0];
  const ultima = ESCALA_VELOCIDAD[ESCALA_VELOCIDAD.length - 1];

  let v = velocidad;
  if (rango) {
    const ancho = rango.max - rango.min;
    const t = ancho > 0 ? (velocidad - rango.min) / ancho : 0;
    v = primera.velocidad + t * (ultima.velocidad - primera.velocidad);
  }

  if (v <= primera.velocidad) return primera.color;
  for (let i = 1; i < ESCALA_VELOCIDAD.length; i++) {
    const hasta = ESCALA_VELOCIDAD[i];
    if (v <= hasta.velocidad) {
      const desde = ESCALA_VELOCIDAD[i - 1];
      return mezclarColores(
        desde.color,
        hasta.color,
        (v - desde.velocidad) / (hasta.velocidad - desde.velocidad),
      );
    }
  }
  return ultima.color;
}

/** Radios de los markers individuales (el halo se calcula como radio * 1.8). */
export const RADIO_MARKER_PARADA = 9;
export const RADIO_MARKER_EXCESO = 8;
export const RADIO_MARKER_EVENTO = 9;
